import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, LayoutDashboard, ListChecks, Instagram, ScrollText, LogOut, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import { Toaster } from "sonner";

const navItems = [
  { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/sources", label: "Sources", icon: ListChecks },
  { to: "/admin/instagram", label: "Instagram", icon: Instagram },
  { to: "/admin/logs", label: "Logs", icon: ScrollText },
];

export default function AdminLayout() {
  const { logout, email } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F7F5F0] flex flex-col lg:flex-row">
      <Toaster position="top-right" richColors />

      {/* Sidebar on desktop, bottom tab bar on mobile */}
      <aside className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[rgba(61,64,53,0.08)] shadow-[0_-8px_24px_rgba(61,64,53,0.08)] lg:static lg:w-60 lg:min-h-screen lg:shrink-0 lg:border-t-0 lg:border-r lg:shadow-none flex lg:flex-col">
        <Link to="/" className="hidden lg:flex items-center gap-2 px-5 h-16 border-b border-[rgba(61,64,53,0.08)]">
          <div className="w-9 h-9 rounded-xl bg-[#E07A5F] flex items-center justify-center text-white">
            <MapPin size={18} strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-heading font-bold leading-none">LunchSpot</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#9A9E8D] mt-0.5">Admin</div>
          </div>
        </Link>
        <nav className="p-2 flex-1 grid grid-cols-4 gap-1 lg:p-3 lg:flex lg:flex-col">
          {navItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={`nav-${it.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex min-w-0 flex-col items-center justify-center gap-1 px-2 py-2 rounded-md text-[11px] font-medium transition-colors lg:flex-row lg:justify-start lg:gap-2 lg:px-3 lg:text-sm ${
                  isActive ? "bg-[#EFEBE1] text-[#3D4035]" : "text-[#6A6F5C] hover:bg-[#F7F5F0]"
                }`
              }
            >
              <it.icon size={16} className="shrink-0" /> <span className="truncate">{it.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:block p-3 border-t border-[rgba(61,64,53,0.08)]">
          <div className="text-xs text-[#6A6F5C] mb-2 truncate" title={email}>{email}</div>
          <Button variant="outline" size="sm" className="w-full" onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout">
            <LogOut size={14} className="mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden pb-24 lg:pb-0">
        <div className="sticky top-0 z-30 bg-[#F7F5F0]/95 backdrop-blur border-b border-[rgba(61,64,53,0.08)] px-4 py-3 sm:px-8">
          <Link
            to="/"
            data-testid="admin-back-to-feed"
            className="inline-flex items-center gap-2 rounded-full border border-[rgba(61,64,53,0.12)] bg-white px-4 py-2 text-sm font-medium text-[#3D4035] shadow-sm transition-colors hover:bg-[#EFEBE1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E07A5F] focus-visible:ring-offset-2"
          >
            <ArrowLeft size={16} className="shrink-0" />
            <span>Zurück zur Speisenübersicht</span>
          </Link>
        </div>
        <Outlet />
      </main>
    </div>
  );
}
